'use client';

import { useState, useEffect, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { motion, useReducedMotion } from 'motion/react';
import { Flame, Shield, User, ArrowRight, LogOut, Check, ChefHat } from 'lucide-react';
import { TENANTS } from '@server/tenants';
import { Modal } from '@components/ui';

interface SessionInfo {
  authenticated: boolean;
  session?: {
    user_id: string;
    restaurant_id: string;
    slug: string;
    role: 'admin' | 'staff';
    name: string;
    email: string;
    host: string;
  };
}

type AccountAccess = {
  name: string;
  email: string;
  role: 'admin' | 'staff';
  restaurant_name: string;
  slug: string;
  password_setup_required: boolean;
  invite_valid: boolean;
};

export default function AuthPage() {
  return (
    <Suspense fallback={<AuthLoading label="A carregar..." />}>
      <AuthPageContent />
    </Suspense>
  );
}

function AuthLoading({ label }: { label: string }) {
  return (
    <div className="auth-shell flex items-center justify-center">
      <p className="text-ink-500 text-sm">{label}</p>
    </div>
  );
}

function AuthPageContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const reduce = useReducedMotion();
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [accountAccess, setAccountAccess] = useState<AccountAccess | null>(null);
  const [accountLoading, setAccountLoading] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [quickLoginVisible, setQuickLoginVisible] = useState(false);
  const [quickLoginTenants, setQuickLoginTenants] = useState<Set<string>>(new Set());
  const [currentHost, setCurrentHost] = useState('');
  const requestedPath = searchParams.get('next');
  const afterLoginPath = requestedPath?.startsWith('/') && !requestedPath.startsWith('//') ? requestedPath : '/admin';
  const accountKey = searchParams.get('account');
  const inviteToken = searchParams.get('invite');

  const isLocalHost = currentHost === 'localhost' || currentHost.endsWith('.localhost');
  const baseDomain = currentHost
    ? (isLocalHost ? 'localhost' : currentHost.split('.').slice(-2).join('.'))
    : 'okai.pt';
  const tenantHost = (slug: string) => `${slug}.${baseDomain}`;
  const tenantUrl = (slug: string, path: string) => {
    if (typeof window === 'undefined') return `https://${tenantHost(slug)}${path}`;
    const port = window.location.port ? `:${window.location.port}` : '';
    return `${window.location.protocol}//${tenantHost(slug)}${port}${path}`;
  };
  const apexUrl = (path: string) => {
    if (typeof window === 'undefined') return `https://${baseDomain}${path}`;
    const port = window.location.port ? `:${window.location.port}` : '';
    return `${window.location.protocol}//${baseDomain}${port}${path}`;
  };
  const currentTenantSlug = Object.keys(TENANTS).find((slug) => tenantHost(slug) === currentHost);
  const isTenantSubdomain = Boolean(currentTenantSlug);
  const currentTenant = currentTenantSlug ? TENANTS[currentTenantSlug] : null;

  useEffect(() => {
    setCurrentHost(window.location.hostname);
  }, []);

  useEffect(() => {
    fetch('/api/auth/quick-access', { cache: 'no-store' })
      .then((r) => r.json())
      .then((data) => {
        setQuickLoginVisible(Boolean(data.enabled));
        if (Array.isArray(data.tenants)) {
          setQuickLoginTenants(new Set(
            data.tenants
              .filter((t: { demo_quick_access?: boolean }) => t.demo_quick_access)
              .map((t: { slug: string }) => t.slug),
          ));
        } else if (data.enabled && data.slug) {
          setQuickLoginTenants(new Set([String(data.slug)]));
        }
      })
      .catch(() => {
        setQuickLoginVisible(false);
      });
  }, []);

  useEffect(() => {
    const quick = searchParams.get('quick');
    if (quickLoginVisible && (quick === 'admin' || quick === 'staff')) {
      setBusy(`quick-${quick}`);
      fetch('/api/auth/quick-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: quick }),
      })
        .then((r) => r.json())
        .then((data) => {
          if (data.success) {
            router.push(afterLoginPath);
          } else {
            setError(data.error || 'Quick login falhou.');
            setLoading(false);
            setBusy(null);
          }
        })
        .catch((err) => {
          setError((err as Error).message);
          setLoading(false);
          setBusy(null);
        });
      return;
    }

    fetch('/api/auth/me')
      .then((r) => r.json())
      .then((data) => setSession(data))
      .finally(() => setLoading(false));
  }, [searchParams, router, afterLoginPath, quickLoginVisible]);

  useEffect(() => {
    if (!accountKey) {
      setAccountAccess(null);
      return;
    }
    setAccountLoading(true);
    const params = new URLSearchParams({ account: accountKey });
    if (inviteToken) params.set('invite', inviteToken);
    fetch(`/api/auth/account?${params.toString()}`)
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Link de acesso invalido.');
        setAccountAccess(data.account);
        setEmail(data.account.email);
      })
      .catch((err) => setError((err as Error).message))
      .finally(() => setAccountLoading(false));
  }, [accountKey, inviteToken]);

  const quickLogin = (slug: string, role: 'admin' | 'staff') => {
    setBusy(`${slug}-${role}`);
    setError(null);
    window.location.href = tenantUrl(slug, `/auth?quick=${role}`);
  };

  const passwordLogin = (slug: string) => {
    setBusy(`${slug}-pwd`);
    setError(null);
    const params = new URLSearchParams({ slug, role: 'admin', email });
    window.location.href = tenantUrl(slug, `/auth?${params.toString()}`);
  };

  const localPasswordLogin = async () => {
    if ((!currentTenantSlug && !accountKey) || (!email && !accountKey) || !password) return;
    setBusy('local-pwd');
    setError(null);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, account_key: accountKey }),
      });
      const data = await res.json();
      if (data.success) {
        router.push(afterLoginPath);
      } else {
        setError(data.error || 'Credenciais invalidas.');
        setBusy(null);
      }
    } catch (err) {
      setError((err as Error).message);
      setBusy(null);
    }
  };

  const activateAccount = async () => {
    if (!accountKey || !inviteToken || newPassword.length < 8 || newPassword !== confirmPassword) return;
    setBusy('activate');
    setError(null);
    const res = await fetch('/api/auth/activate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ account_key: accountKey, invite_token: inviteToken, password: newPassword }),
    });
    const data = await res.json();
    if (res.ok && data.success) {
      router.push(afterLoginPath);
    } else {
      setError(data.error || 'Nao foi possivel ativar o acesso.');
      setBusy(null);
    }
  };

  const logout = async () => {
    await fetch('/api/auth/logout', { method: 'POST' });
    setSession({ authenticated: false });
    router.refresh();
  };

  if (loading || accountLoading) {
    return <AuthLoading label={busy ? 'A entrar...' : 'A carregar...'} />;
  }

  if (session?.authenticated && session.session) {
    const s = session.session;
    const tenant = TENANTS[s.slug];
    return (
      <div className="auth-shell flex items-center justify-center p-4">
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="auth-card text-center"
        >
          <div
            className="w-14 h-14 mx-auto mb-4 rounded-2xl flex items-center justify-center"
            style={{ background: tenant ? `linear-gradient(135deg, ${tenant.primaryColor}, ${tenant.secondaryColor})` : undefined }}
          >
            <Check className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold font-display text-ink-900 text-balance">Sessao ativa</h1>
          <p className="text-ink-600 mt-1 mb-6">{s.name}</p>
          <dl className="bg-surface-2 rounded-xl p-4 mb-6 text-left space-y-2 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-ink-500">Restaurante</dt>
              <dd className="font-medium text-ink-900">{tenant?.name ?? s.slug}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-ink-500">Perfil</dt>
              <dd className="font-medium text-ink-900 capitalize">{s.role}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-ink-500">Email</dt>
              <dd className="font-medium text-ink-900 truncate">{s.email}</dd>
            </div>
          </dl>
          <div className="space-y-3">
            <button type="button" onClick={() => router.push('/admin')} className="btn btn-primary w-full">
              Abrir painel
            </button>
            <button type="button" onClick={logout} className="btn btn-secondary w-full flex items-center justify-center gap-2">
              <LogOut className="w-4 h-4" /> Terminar sessao
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="auth-shell">
      <div className="auth-split">
        <aside
          className="auth-brand-panel auth-brand-panel--solid"
          style={{ background: 'linear-gradient(145deg, #181820 0%, #2a2a35 50%, #121218 100%)' }}
        >
          <div className="auth-brand-content">
            <div className="auth-brand-mark">
              <Flame className="w-7 h-7 text-white" />
            </div>
            <h1 className="auth-brand-title">Okai para restaurantes</h1>
            <p className="auth-brand-tagline">
              Pedidos, chamadas de voz, fila de espera e ementa num unico painel pensado para a cozinha e o balcao.
            </p>
          </div>
          <div className="auth-brand-content flex items-center gap-3 text-sm text-white/70">
            <ChefHat className="w-5 h-5 flex-shrink-0" />
            <span>Multi-tenant. Cada restaurante com o seu espaco e marca.</span>
          </div>
        </aside>

        <div className="auth-form-panel">
          <div className={isTenantSubdomain ? 'auth-card' : 'auth-card-wide'}>
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-2 lg:hidden">
                <div className="w-10 h-10 rounded-xl bg-brand flex items-center justify-center">
                  <Flame className="w-5 h-5 text-brand-contrast" />
                </div>
                <span className="font-display font-bold text-xl text-ink-900">Okai</span>
              </div>
              <h2 className="text-2xl font-bold font-display text-ink-900 text-balance">
                {accountAccess?.restaurant_name ?? (isTenantSubdomain && currentTenant ? currentTenant.name : 'Escolhe o restaurante')}
              </h2>
              <p className="text-ink-600 mt-1 text-sm leading-relaxed max-w-prose">
                {accountAccess
                  ? 'Acesso individual e autonomo.'
                  : isTenantSubdomain && currentTenant
                  ? currentTenant.tagline
                  : 'Acede ao painel de gestao do teu espaco.'}
              </p>
            </div>

            {error && (
              <div className="mb-5 bg-danger-soft border border-danger/20 text-danger rounded-xl p-3 text-sm" role="alert">
                {error}
              </div>
            )}

            {accountAccess && (
              accountAccess?.password_setup_required ? (
                <FirstAccessCard
                  account={accountAccess}
                  password={newPassword}
                  confirmPassword={confirmPassword}
                  busy={busy === 'activate'}
                  onPassword={setNewPassword}
                  onConfirmPassword={setConfirmPassword}
                  onActivate={activateAccount}
                />
              ) : (
                <IndividualLoginCard
                  account={accountAccess}
                  busy={busy}
                  password={password}
                  onPassword={setPassword}
                  onPasswordLogin={localPasswordLogin}
                />
              )
            )}

            {!accountAccess && isTenantSubdomain && currentTenantSlug && currentTenant && (
              <TenantLoginCard
                tenant={currentTenant}
                slug={currentTenantSlug}
                busy={busy}
                email={email}
                password={password}
                account={null}
                onEmail={setEmail}
                onPassword={setPassword}
                onQuickAdmin={() => quickLogin(currentTenantSlug, 'admin')}
                onQuickStaff={() => quickLogin(currentTenantSlug, 'staff')}
                onPasswordLogin={localPasswordLogin}
                apexAuthUrl={apexUrl('/auth')}
                showQuickLogin={quickLoginVisible}
              />
            )}

            {!isTenantSubdomain && (
              <div className="auth-tenant-grid">
                {Object.values(TENANTS).map((t, i) => (
                  <motion.article
                    key={t.slug}
                    initial={reduce ? false : { opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05, duration: 0.25 }}
                    className="auth-tenant-card"
                  >
                    <div
                      className="auth-tenant-header"
                      style={{ background: `linear-gradient(135deg, ${t.primaryColor}, ${t.secondaryColor})` }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                          <Flame className="w-5 h-5 text-white" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-bold text-white truncate">{t.name}</h3>
                          <p className="text-white/80 text-sm truncate">{t.city}</p>
                        </div>
                      </div>
                    </div>
                    <div className="auth-tenant-body">
                      <p className="text-xs text-ink-500 font-mono">{t.slug}.{baseDomain}</p>
                      {(quickLoginTenants.has(t.slug) || (quickLoginVisible && quickLoginTenants.size === 0)) && (
                        <>
                          <button
                            type="button"
                            onClick={() => quickLogin(t.slug, 'admin')}
                            disabled={busy === `${t.slug}-admin`}
                            className="auth-role-btn auth-role-btn-primary"
                          >
                            <span className="flex items-center gap-2"><Shield className="w-4 h-4" /> Admin</span>
                            <ArrowRight className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => quickLogin(t.slug, 'staff')}
                            disabled={busy === `${t.slug}-staff`}
                            className="auth-role-btn auth-role-btn-secondary"
                          >
                            <span className="flex items-center gap-2"><User className="w-4 h-4" /> Funcionario</span>
                            <ArrowRight className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setShowPassword(t.slug);
                          setEmail('');
                          setError(null);
                        }}
                        className="min-h-11 w-full rounded-lg px-1 text-left text-sm font-medium text-ink-500 transition-colors hover:text-ink-900"
                      >
                        Entrar com palavra-passe
                      </button>
                    </div>
                  </motion.article>
                ))}

                <motion.article
                  initial={reduce ? false : { opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Object.keys(TENANTS).length * 0.05 }}
                  className="auth-tenant-card border-ink-900/15"
                >
                  <div className="auth-tenant-header bg-ink-900">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center">
                        <Shield className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-white">Super Admin</h3>
                        <p className="text-white/70 text-sm">Plataforma Okai</p>
                      </div>
                    </div>
                  </div>
                  <div className="auth-tenant-body">
                    <p className="text-xs text-ink-500 font-mono">{baseDomain}/super-admin</p>
                    <a href="/super-admin/auth" className="auth-role-btn auth-role-btn-primary">
                      <span className="flex items-center gap-2"><Shield className="w-4 h-4" /> Painel da plataforma</span>
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </motion.article>
              </div>
            )}

            <Modal
              open={Boolean(showPassword)}
              onClose={() => {
                setShowPassword(null);
                setEmail('');
              }}
              title={showPassword ? `Entrar em ${TENANTS[showPassword]?.name ?? 'restaurante'}` : 'Entrar'}
              size="sm"
              footer={(
                <>
                  <button
                    type="button"
                    className="btn btn-secondary btn-md"
                    onClick={() => setShowPassword(null)}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-md"
                    disabled={!email || !showPassword}
                    onClick={() => showPassword && passwordLogin(showPassword)}
                  >
                    Continuar
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </>
              )}
            >
              <p className="mb-5 text-sm leading-relaxed text-ink-500">
                Indica o email do teu acesso. A palavra-passe é pedida no domínio privado do restaurante.
              </p>
              <label className="input-label" htmlFor="tenant-access-email">Email</label>
              <input
                id="tenant-access-email"
                type="email"
                autoFocus
                data-autofocus
                autoComplete="email"
                placeholder="email@exemplo.pt"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && email && showPassword) passwordLogin(showPassword);
                }}
                className="input-field"
              />
            </Modal>

            <p className="text-center text-ink-500 text-xs mt-8 leading-relaxed">
              Acesso protegido por credenciais individuais de cada tenant.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function TenantLoginCard({
  tenant,
  slug,
  busy,
  email,
  password,
  account,
  onEmail,
  onPassword,
  onQuickAdmin,
  onQuickStaff,
  onPasswordLogin,
  apexAuthUrl,
  showQuickLogin,
}: {
  tenant: (typeof TENANTS)[string];
  slug: string;
  busy: string | null;
  email: string;
  password: string;
  account: AccountAccess | null;
  onEmail: (v: string) => void;
  onPassword: (v: string) => void;
  onQuickAdmin: () => void;
  onQuickStaff: () => void;
  onPasswordLogin: () => void;
  apexAuthUrl: string;
  showQuickLogin: boolean;
}) {
  return (
    <div className="space-y-3">
      <div
        className="rounded-xl p-4 text-white"
        style={{ background: `linear-gradient(135deg, ${tenant.primaryColor}, ${tenant.secondaryColor})` }}
      >
        <p className="text-sm text-white/80">{tenant.city}</p>
        {account && (
          <div className="mt-2">
            <p className="font-semibold">{account.name}</p>
            <p className="text-sm text-white/80">{account.role === 'admin' ? 'Administrador' : 'Funcionario'} · {account.email}</p>
          </div>
        )}
      </div>
      {showQuickLogin && (
        <>
          <button
            type="button"
            onClick={onQuickAdmin}
            disabled={busy === `${slug}-admin`}
            className="auth-role-btn auth-role-btn-primary"
          >
            <span className="flex items-center gap-2"><Shield className="w-4 h-4" /> Entrar como admin</span>
            <ArrowRight className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onQuickStaff}
            disabled={busy === `${slug}-staff`}
            className="auth-role-btn auth-role-btn-secondary"
          >
            <span className="flex items-center gap-2"><User className="w-4 h-4" /> Entrar como funcionario</span>
            <ArrowRight className="w-4 h-4" />
          </button>
          <div className="auth-divider">ou com password</div>
        </>
      )}
      <div className="space-y-2">
        {!account && (
          <>
            <label className="input-label" htmlFor="tenant-email">Email</label>
            <input
              id="tenant-email"
              type="email"
              placeholder="email@exemplo.pt"
              value={email}
              onChange={(e) => onEmail(e.target.value)}
              className="input-field text-sm"
            />
          </>
        )}
        <label className="input-label" htmlFor="tenant-password">Password</label>
        <input
          id="tenant-password"
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => onPassword(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onPasswordLogin()}
          className="input-field text-sm"
        />
        <button
          type="button"
          onClick={onPasswordLogin}
          disabled={busy === 'local-pwd' || (!account && !email) || !password}
          className="btn btn-secondary w-full text-sm disabled:opacity-50"
        >
          Entrar
        </button>
      </div>
      <p className="text-center pt-2">
        <a href={apexAuthUrl} className="text-sm text-ink-500 hover:text-ink-900 transition-colors">
          Ver todos os restaurantes
        </a>
      </p>
    </div>
  );
}

function FirstAccessCard({
  account,
  password,
  confirmPassword,
  busy,
  onPassword,
  onConfirmPassword,
  onActivate,
}: {
  account: AccountAccess;
  password: string;
  confirmPassword: string;
  busy: boolean;
  onPassword: (value: string) => void;
  onConfirmPassword: (value: string) => void;
  onActivate: () => void;
}) {
  if (!account.invite_valid) {
    return (
      <div className="rounded-xl border border-warning/30 bg-warning-soft p-4 text-sm text-ink-700">
        Este convite expirou ou ja foi utilizado. Pede a um administrador para reenviar o convite individual.
      </div>
    );
  }

  const passwordsMatch = password === confirmPassword;
  return (
    <div className="space-y-4">
      <div className="rounded-xl bg-surface-2 p-4">
        <p className="font-semibold text-ink-900">{account.name}</p>
        <p className="text-sm text-ink-500">{account.role === 'admin' ? 'Administrador' : 'Funcionario'} · {account.email}</p>
      </div>
      <p className="text-sm text-ink-600">Define uma palavra-passe apenas para este acesso.</p>
      <div className="space-y-2">
        <label className="input-label" htmlFor="new-password">Nova palavra-passe</label>
        <input id="new-password" type="password" minLength={8} autoComplete="new-password" value={password} onChange={(e) => onPassword(e.target.value)} className="input-field" />
        <label className="input-label" htmlFor="confirm-password">Confirmar palavra-passe</label>
        <input id="confirm-password" type="password" minLength={8} autoComplete="new-password" value={confirmPassword} onChange={(e) => onConfirmPassword(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && onActivate()} className="input-field" />
        {confirmPassword && !passwordsMatch && <p className="text-xs text-danger">As palavras-passe nao coincidem.</p>}
      </div>
      <button type="button" onClick={onActivate} disabled={busy || password.length < 8 || !passwordsMatch} className="btn btn-primary w-full disabled:opacity-50">
        {busy ? 'A ativar...' : 'Definir palavra-passe e entrar'}
      </button>
    </div>
  );
}

function IndividualLoginCard({
  account,
  password,
  busy,
  onPassword,
  onPasswordLogin,
}: {
  account: AccountAccess;
  password: string;
  busy: string | null;
  onPassword: (value: string) => void;
  onPasswordLogin: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-xl bg-surface-2 p-4">
        <p className="font-semibold text-ink-900">{account.name}</p>
        <p className="text-sm text-ink-500">{account.role === 'admin' ? 'Administrador' : 'Funcionario'} · {account.email}</p>
      </div>
      <div className="space-y-2">
        <label className="input-label" htmlFor="individual-password">Palavra-passe</label>
        <input id="individual-password" type="password" autoComplete="current-password" value={password} onChange={(e) => onPassword(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && onPasswordLogin()} className="input-field" />
      </div>
      <button type="button" onClick={onPasswordLogin} disabled={busy === 'local-pwd' || !password} className="btn btn-primary w-full disabled:opacity-50">
        {busy === 'local-pwd' ? 'A entrar...' : 'Entrar neste acesso'}
      </button>
    </div>
  );
}
